import { Tree, TreeItem, TreeItemGroup } from '@angular/aria/tree';
import { CdkMonitorFocus } from '@angular/cdk/a11y';
import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
	Component,
	effect,
	input,
	signal,
	ViewEncapsulation
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import {
	isActive,
	IsActiveMatchOptions,
	NavigationEnd,
	Router,
	RouterLink,
	RouterLinkActive
} from '@angular/router';
import { NavigationItem } from 'app/core/navigation/navigation.types';
import { filter, take } from 'rxjs';

@Component({
	selector:      'app-navigation',
	templateUrl:   './navigation.component.html',
	encapsulation: ViewEncapsulation.None,
	imports:       [
		CdkMonitorFocus,
		MatIconModule,
		NgFor,
		NgIf,
		NgTemplateOutlet,
		RouterLink,
		RouterLinkActive,
		Tree,
		TreeItem,
		TreeItemGroup
	]
})
export class NavigationComponent {
	public items = input<NavigationItem[]>([]);

	protected navigation = signal<NavigationItem[]>([]);

	private readonly navigationEnd;

	constructor(private readonly router: Router) {
		this.navigationEnd = toSignal(
			this.router.events.pipe(
				filter(event => event instanceof NavigationEnd),
				take(1)
			)
		);

		effect(() => {
			const items = structuredClone(this.items() ?? []);

			this.navigation.set(
				this.navigationEnd() ? this.expandActiveRoute(items) : items
			);
		});
	}

	protected trackById(index: number, item: NavigationItem): string {
		return item.id;
	}

	/**
	 * Expand all parent routes of the active route.
	 */
	private expandActiveRoute(items: NavigationItem[]): NavigationItem[] {
		for (const item of items) {
			if (item.children?.length) {
				item.children = this.expandActiveRoute(item.children);

				if (item.children.some(child => child.expanded)) {
					item.expanded = true;
				}
			}

			if (
				item.route
				&& isActive(
					item.route,
					this.router,
					this.isActiveOption(item.activeOptions ?? { exact: true })
				)()
			) {
				item.expanded = true;
			}
		}

		return items;
	}

	/**
	 * Convert simple exact option to full IsActiveMatchOptions.
	 */
	private isActiveOption(
		options: { exact: boolean } | IsActiveMatchOptions
	): IsActiveMatchOptions {
		if ('exact' in options) {
			return options.exact
				? {
						paths:        'exact',
						queryParams:  'exact',
						fragment:     'ignored',
						matrixParams: 'ignored'
					}
				: {
						paths:        'subset',
						queryParams:  'subset',
						fragment:     'ignored',
						matrixParams: 'ignored'
					};
		}

		return options;
	}
}
